import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import Index from "./pages/Index";
import GIVFPage from "./pages/givf";
import TeamPage from "./pages/about/TeamPage";
import CompanyPage from "./pages/about/CompanyPage";
import PrivacyPage from "./pages/terms/PrivacyPage";
import ContactPage from "./pages/ContactPage";
import UserDashboard from "./pages/UserDashboard";
import PublicEventPage from "./pages/events/PublicEventPage";
import OpportunitiesPage from "./pages/opportunities/OpportunitiesPage";
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";
import VerificationPending from "./pages/auth/VerificationPending";
import { Toaster } from "./components/ui/toaster";
import { Footer } from "./components/Footer";
import OpportunityDetailsPage from "./pages/opportunities/OpportunityDetailsPage";
import SkuOpportunityDetailsPage from "./pages/opportunities/SkuOpportunityDetailsPage";
import NPOSkuOpportunitiesPage from "@/pages/opportunities/NPOSkuOpportunitiesPage";
import TermsPage from "./pages/terms/TermsPage";
import AnonCheckout from "./pages/AnonCheckout";
import ThankYouPage from "./pages/checkout/ThankYouPage";
import GetStartedPage from "./pages/GetStartedPage";
import PaymentFailedPage from "./pages/checkout/PaymentFailedPage";
import VolunteerEngagementPage from "./pages/solutions/VolunteerEngagementPage";
import MarketingAndOutreachPage from "./pages/solutions/MarketingAndOutreachPage";
import ImpactReportsPage from "./pages/solutions/ImpactReportsPage";
import CommunityVolunteeringPage from "@/pages/solutions/CommunityVolunteeringPage";
import ChatSupportPage from "./pages/solutions/ChatSupportPage";
import CorporateVolunteeringPage from "./pages/solutions/CorporateVolunteeringPage";
import { FAQSection } from "./components/home/FAQSection";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import GuidesPage from "./pages/guides/GuidesPage";
import ChatbotsForNonprofits from "./pages/guides/ChatbotsForNonprofits";
import AIContentTools from "./pages/guides/AIContentTools";
import AIToolsForNonprofits from "./pages/guides/AIToolsForNonprofits";
import TechGrantPage from "./pages/guides/TechGrantPage";
import SEO from "@/components/SEO";
import AuthPages from "./pages/auth/AuthPages";
import ResetPassword from "./pages/auth/ResetPassword";
import { useAuth } from "./hooks/useAuth";
import { ScrollToTop } from "./components/ScrollToTop";
import { AIAssistantPage } from "@/pages/solutions/AIAssistantPage";
import { KnowledgeHubPage } from "@/pages/solutions/KnowledgeHubPage";
import { MissionHubPage } from "@/pages/solutions/MissionHubPage";
import { AIGrowthEnginePage } from "@/pages/solutions/AIGrowthEnginePage";
import { ChatWidget } from "./pages/chat";
import { useEffect, useState } from "react";
import CorporateCampaignMicrosite from "./pages/corporate/CorporateCampaignMicrosite";
import EventBookingPage from "./pages/corporate/EventBookingPage";
import ResourcesBudgetCalculator from "./pages/ResourcesBudgetCalculator";
import CoraaAIWebinarPage from "./pages/resources/coraa-ai-webinar";
import NPOlandingUS from "./pages/NPOlandingUS";
import UNCampaignPlanner from "./pages/resources/un-campaign-planner";
import CarbonOffsetEstimatorPage from "./pages/resources/CarbonOffsetEstimatorPage";
import DisasterResponseGuide from "./pages/guides/disaster-response-guide";
import Corporate from "./pages/Corporate";
import CoraaWidget from "./components/coraa/CoraaWidget";
import CoraaAILandingPage from "./pages/resources/coraa-ai-landing";
import ResourcesPage from "./pages/resources/ResourcesPage";

// Component to conditionally render ChatWidget based on current path
function ConditionalChatWidget() {
  const location = useLocation();

  // Hide chat on feedback pages
  const isDashboardPage = location.pathname.startsWith("/dashboard");

  if (isDashboardPage) {
    return null;
  }

  return null;
}

function App() {
  const {
    profile,
    session,
    isLoading,
    redirectToLogin,
    isTokenExpired,
    refreshToken,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMicrosite, setMicrosite] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/corporate/") || path.startsWith("/events/")) {
      setMicrosite(true);
      return;
    }
    setMicrosite(false);
    if (isLoading) return;
    if (session) {
      if (isTokenExpired(session)) {
        refreshToken(session.refresh_token);
        window.location.reload();
        return;
      }
    }
    if (!session && !profile && path.startsWith("/dashboard")) {
      console.log("Redirecting to login");
      redirectToLogin();
      return;
    }
    if (path === "/dashboard") {
      navigate("/dashboard");
    }
  }, [location.pathname, profile, session, isLoading]);

  return isMicrosite ? (
    <Routes>
      <Route
        path="/corporate/:corporateName/events/:campaignId/book/:opportunityId"
        element={
          <div className="min-h-screen">
            <EventBookingPage />
          </div>
        }
      />
      <Route
        path="/corporate/:corporateName/events/:campaignId"
        element={
          <div className="min-h-screen">
            <CorporateCampaignMicrosite />
          </div>
        }
      />
      <Route
        path="/events/great-india-volunteering-festival-tgivf-2025"
        element={<GIVFPage />}
      />
    </Routes>
  ) : isLoading ? (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  ) : (
    <div className="min-h-screen bg-gray-50">
      <ScrollToTop />
      <Navbar profile={profile} session={session} />
      <SEO />
      <Routes>
        <Route
          path="/dashboard/*"
          element={<UserDashboard profile={profile} session={session} />}
        />
        <Route path="/login" element={<AuthPages />} />
        <Route path="/signup" element={<AuthPages />} />
        <Route path="/signup/nonprofit" element={<AuthPages />} />
        <Route path="/signup/corporate" element={<AuthPages />} />
        <Route path="/signup/host" element={<AuthPages />} />
        <Route path="/forgot-password" element={<AuthPages />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/verification-pending"
          element={<VerificationPending session={session} profile={profile} />}
        />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/events/:slug" element={<PublicEventPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route
          path="/checkout/:npoSlug/:catalogSlug"
          element={<AnonCheckout />}
        />
        <Route path="/checkout/thank-you" element={<ThankYouPage />} />
        <Route
          path="/checkout/payment-failed"
          element={<PaymentFailedPage />}
        />
        <Route path="/opportunities" element={<OpportunitiesPage />} />
        <Route
          path="/opportunities/npo/:npoSlug"
          element={
            <NPOSkuOpportunitiesPage session={session} profile={profile} />
          }
        />
        <Route
          path="/opportunities/:npoSlug/:catalogSlug"
          element={
            <OpportunityDetailsPage profile={profile} session={session} />
          }
        />
        <Route
          path="/opportunities/:id"
          element={
            <SkuOpportunityDetailsPage profile={profile} session={session} />
          }
        />
        <Route path="/" element={<CoraaAIWebinarPage />} />

        <Route
          path="/resources/budget-calculator"
          element={<ResourcesBudgetCalculator />}
        />

        <Route
          path="/resources/un-campaign-planner"
          element={<UNCampaignPlanner />}
        />
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route
          path="/solutions/volunteer-engagement"
          element={<VolunteerEngagementPage />}
        />
        <Route
          path="/solutions/marketing-and-outreach"
          element={<MarketingAndOutreachPage />}
        />
        <Route
          path="/solutions/impact-reports-and-storytelling"
          element={<ImpactReportsPage />}
        />
        <Route
          path="/solutions/community-volunteering"
          element={<CommunityVolunteeringPage />}
        />
        <Route
          path="/solutions/chat-support-leads-and-engagement"
          element={<ChatSupportPage />}
        />
        <Route
          path="/solutions/corporate-volunteering"
          element={<CorporateVolunteeringPage />}
        />
        <Route path="/solutions/ai-assistant" element={<AIAssistantPage />} />
        <Route path="/solutions/knowledge-hub" element={<KnowledgeHubPage />} />
        <Route path="/solutions/mission-hub" element={<MissionHubPage />} />
        <Route
          path="/solutions/ai-growth-engine"
          element={<AIGrowthEnginePage />}
        />
        <Route path="/about/team" element={<TeamPage />} />
        <Route path="/about/company" element={<CompanyPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/help/faq" element={<FAQSection />} />
        <Route path="/guides" element={<GuidesPage />} />

        <Route
          path="/solutions/volunteer-engagement"
          element={<VolunteerEngagementPage />}
        />
        <Route
          path="/solutions/marketing-and-outreach"
          element={<MarketingAndOutreachPage />}
        />
        <Route
          path="/solutions/impact-reports-and-storytelling"
          element={<ImpactReportsPage />}
        />
        <Route
          path="/solutions/community-volunteering"
          element={<CommunityVolunteeringPage />}
        />
        <Route
          path="/solutions/chat-support-leads-and-engagement"
          element={<ChatSupportPage />}
        />
        <Route
          path="/solutions/corporate-volunteering"
          element={<CorporateVolunteeringPage />}
        />
        <Route path="/solutions/ai-assistant" element={<AIAssistantPage />} />
        <Route path="/solutions/knowledge-hub" element={<KnowledgeHubPage />} />
        <Route path="/solutions/mission-hub" element={<MissionHubPage />} />
        <Route
          path="/solutions/ai-growth-engine"
          element={<AIGrowthEnginePage />}
        />
        <Route path="/corporate" element={<Corporate />} />
        <Route path="/about/team" element={<TeamPage />} />
        <Route path="/about/company" element={<CompanyPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/help/faq" element={<FAQSection />} />
        <Route path="/guides" element={<GuidesPage />} />
        <Route
          path="/guides/disaster-response-guide"
          element={<DisasterResponseGuide />}
        />
        <Route
          path="/guides/chatbots-for-nonprofits"
          element={<ChatbotsForNonprofits />}
        />
        <Route path="/guides/ai-content-tools" element={<AIContentTools />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route
          path="/resources/CarbonOffsetEstimatorPage"
          element={<CarbonOffsetEstimatorPage />}
        />
        <Route path="/nonprofit" element={<NPOlandingUS />} />
        <Route path="/tech-grant" element={<TechGrantPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
      <ConditionalChatWidget />
      <Footer profile={profile} session={session} />
    </div>
  );
}

export default App;
